import React from 'react';
import { sortBy, forOwn, get, isString, size, upperCase, isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment/moment';
import { TableSelect } from '../../../screens/ChargersList/components/ChargersTableComponents';
import {
  cConditionOptions,
  chargingChildren,
  statusMenuItems,
  StatusLabel,
  chargerStateLabels,
} from '../../constants/CommonUsedDropdownsStatics';
import { CHARGER_STATUS_UNKNOWN, configurationStatuses, statusLabels } from '../../constants/statuses';
import { onDatePickerFilter, onTextFilter } from '../CommonTableDataFilter/CommonTableDataFilter';
import { iotOnDateFormat } from '../../services/ValidationRules';
import { getDurationWithMainHours } from '../../services/DateUtils';
import MenuItem from '../MenuItem/MenuItem';
import { getConnectorsNumberToShow, makeSelectInputOptions } from '../../services/ChargerFormBuilidingService';
import uiLabelsPaths from '../../services/uiFieldLabelsDictionary';
import { isOnlyMobile } from '../../services/isMobile';
import { StatusCell } from '../StatusCell';
import InfoButtonComponent from '../../../screens/HomePage/components/InfoButtonComponent';
import { isDemo } from '../Echarts/helpers/helper';
import { INACTIVE_LABEL } from '../../../screens/AdminTools/components/Dictionary/components/DictionaryStatics';
import { setEnergyOutput } from '../../resources/helper';

export const allMenuItem = (
  <MenuItem key="All" value="" title="All">
    All
  </MenuItem>
);

export const getProductTypeOptions = (values) => {
  const productTypes = [allMenuItem];

  productTypes.push(
    values.map((item) => (
      <MenuItem key={uuidv4()} value={item.value} title={item.label || item.value}>
        {item.label || item.value}
      </MenuItem>
    )),
  );

  return productTypes;
};

const conditionOpts = cConditionOptions('create');
const filterConditionOpts = cConditionOptions('filter');

conditionOpts.unshift({ value: '', label: 'All' });
filterConditionOpts.unshift({ value: '', label: 'All' });

export const conditionOptions = conditionOpts.map((item) => (
  <MenuItem key={uuidv4()} value={item.value} title={item.label}>
    {item.label}
  </MenuItem>
));

export const filterConditionOptions = filterConditionOpts.map((item) => (
  <MenuItem key={uuidv4()} value={item.value.toUpperCase()} title={item.label}>
    {item.label}
  </MenuItem>
));

export const getMenuItemsFromDictValues = (countries) =>
  countries.map((item) => {
    const conditionInactive = item.status === 'INACTIVE' ? INACTIVE_LABEL : '';

    return { value: item.code || item.value, label: `${item.value} ${conditionInactive}` };
  });

const statusesVal = statusMenuItems();

statusesVal.unshift({ value: '', label: 'All' });
export const chargerStatusOptions = statusesVal.map((item) => (
  <MenuItem key={uuidv4()} value={item.value.toUpperCase()} title={item.label}>
    {item.label}
  </MenuItem>
));

export const chargingOptions = [
  allMenuItem,
  ...chargingChildren.map(({ value, label }) => (
    <MenuItem key={uuidv4()} value={value} title={label}>
      <StatusCell labelClassName={`c_charging-label-${value.toLowerCase()}`} labelText={label} inDropdown />
    </MenuItem>
  )),
];

export const configurationOptions = [
  allMenuItem,
  ...configurationStatuses.map(({ key, label }) => (
    <MenuItem key={uuidv4()} value={key} title={label}>
      <StatusCell labelClassName={`c_configuration-label-${key.toLowerCase()}`} labelText={label} inDropdown />
    </MenuItem>
  )),
];

export const chargerStatuses = [
  {
    key: ['APPLIED'],
    label: 'Applied',
  },
  {
    key: ['CANCELLED'],
    label: 'Cancelled',
  },
  {
    key: ['ERROR', 'FAILED'],
    label: 'Error',
  },
  {
    key: ['IN_PROGRESS', 'SENT'],
    label: 'In Progress',
  },
  {
    key: ['MANUFACTURED'],
    label: 'Manufactured',
  },
  {
    key: ['RESPONSE_TIME_OUT'],
    label: 'Response Time Out',
  },
  {
    key: ['POSTPONED'],
    label: 'Scheduled',
  },
  {
    key: ['TIME_OUT'],
    label: 'Timed Out',
  },
  {
    key: ['QUEUED'],
    label: 'Queued',
  },
  {
    key: ['UNRESPONSIVE'],
    label: 'Unresponsive',
  },
];

export const statusOptions = [
  allMenuItem,
  ...chargerStatuses.map(({ key, label }) => (
    <MenuItem key={uuidv4()} value={key} title={label}>
      <StatusCell labelClassName={`c_fw-update-label-${key.join('-').toLowerCase()}`} labelText={label} inDropdown />
    </MenuItem>
  )),
];

export const onlineOptions = [
  allMenuItem,
  <MenuItem key={uuidv4()} value title="Yes">
    <StatusCell labelClassName="c_online-label-yes" labelText="Yes" inDropdown />
  </MenuItem>,
  <MenuItem key={uuidv4()} value={false} title="No">
    <StatusCell labelClassName="c_online-label-no" labelText="No" inDropdown />
  </MenuItem>,
];

export const chargingModes = {
  NORMAL: '0 - Normal Setting',
  SOC: '1 - SOC Setting',
  TIME: '2 - Charge Time Setting',
  ENERGY: '3 - Energy Setting',
};

const sessionStatusesValues = {
  OK: 'Ok',
  IN_PROGRESS: 'In Progress',
  LOCAL_STOP: 'Local Stop',
  REMOTE_STOP: 'Remote Stop',
  START_ERROR: 'Start Error',
  STOP_ERROR: 'Stop Error',
  ERROR: 'Error',
  EV_DISCONNECTED: 'EV Disconnected',
  EMERGENCY_STOP: 'Emergency Stop',
  ENERGY_LIMIT_REACHED: 'Energy Limit Reached',
  POWER_LOSS: 'Power Loss',
  REBOOT: 'Reboot',
  SOFT_RESET: 'Soft Reset',
  HARD_RESET: 'Hard Reset',
  UNLOCK_COMMAND: 'Unlock Command',
  DE_AUTHORIZED: 'Deauthorized',
  CLOUD_CMD: 'Cloud Cmd',
  OTHER: 'Other',
  FORCED_STOP: 'Forced Stop',
  SUSPENDED_EVSE: 'Suspended EVSE',
  SUSPENDED_EV: 'Suspended EV',
};

export const sessionStatuses = Object.keys(sessionStatusesValues)
  .sort()
  .reduce((obj, key) => {
    obj[key] = sessionStatusesValues[key];

    return obj;
  }, {});

const LINK_COLUMN_HEADER_CLASS = 'table-link-header';
const LINK_COLUMN_CELL_CLASS = 'table-link-cell';
const CHECKBOX_COLUMN_HEADER_CLASS = 'checkbox-cell-header';
const CHECKBOX_COLUMN_CELL_CLASS = 'checkbox-cell';
const STICKY_COLUMN_HEADER_CLASS = 'sticky-header';
const STICKY_COLUMN_CELL_CLASS = 'sticky-cell';
const FREE_HEIGHT_CELL_CLASS = 'free-height';
const CHECKBOX_COLUMN_WIDTH = 65;

export const getColumnClasses = ({
  isLink = false,
  isCheckbox = false,
  isSticky = false,
  customHeaderClassName = '',
  customCellClassName = '',
  isFreeHeight = false,
}) => {
  const headerClassNames = [];
  const cellClassNames = [];

  if (customHeaderClassName) {
    headerClassNames.push(customHeaderClassName);
  }

  if (customCellClassName) {
    cellClassNames.push(customCellClassName);
  }

  if (isLink) {
    headerClassNames.push(LINK_COLUMN_HEADER_CLASS);
    cellClassNames.push(LINK_COLUMN_CELL_CLASS);
  }

  if (isCheckbox) {
    headerClassNames.push(CHECKBOX_COLUMN_HEADER_CLASS);
    cellClassNames.push(CHECKBOX_COLUMN_CELL_CLASS);
  }

  if (isSticky) {
    headerClassNames.push(STICKY_COLUMN_HEADER_CLASS);
    cellClassNames.push(STICKY_COLUMN_CELL_CLASS);
  }

  if (isFreeHeight) {
    cellClassNames.push(FREE_HEIGHT_CELL_CLASS);
  }

  const classes = {};

  if (headerClassNames.length) {
    classes.headerClassName = headerClassNames.join(' ');
  }

  if (cellClassNames.length) {
    classes.className = cellClassNames.join(' ');
  }

  return classes;
};

export const getCheckboxColumn = (classes = {}) => ({
  id: 'checkbox',
  accessor: '',
  ...getColumnClasses({ ...classes, isCheckbox: true, isSticky: !isOnlyMobile }),
  filterable: false,
  sortable: false,
  resizable: false,
  width: CHECKBOX_COLUMN_WIDTH,
});

export const buildChargingModes = () => {
  const result = [allMenuItem];

  forOwn(chargingModes, (val, key) => {
    result.push(
      <MenuItem key={key} value={key} title={val}>
        {val}
      </MenuItem>,
    );
  });

  return result;
};

export const buildSessionStatus = (original) => {
  const errorsString = get(original, 'error_code');
  const status = get(original, 'cs_status');
  const label = sessionStatuses[status] ? sessionStatuses[status] : 'Unknown';

  return isString(errorsString) && errorsString.length ? `${label} (${errorsString.replace(',', ', ')})` : label;
};

export const getChargerStateColumn = (uiLabels, accessor = 'charger_state', onlinePath = 'i2p2_state.c_online') => ({
  Header: get(uiLabels, uiLabelsPaths.connector_state, 'Present Charger State'),
  id: 'charger_state',
  accessor: (charger) => {
    const chargerState = get(charger, accessor);
    const isOnline = get(charger, onlinePath);

    return isOnline ? chargerState : CHARGER_STATUS_UNKNOWN.toUpperCase();
  },
  minWidth: 150,
  Filter: ({ filter, onChange }) => <TableSelect options={chargingOptions} onChange={onChange} value={filter ? filter.value : ''} />,
  Cell: ({ value }) => <StatusLabel status={chargerStateLabels[value]} />,
});

export const getChargerStatusColumn = (uiLabels, statusPath = 'i2p2_state.c_status', onlinePath = 'i2p2_state.c_online') => ({
  Header: get(uiLabels, uiLabelsPaths.c_status, 'Charger Status'),
  id: 'status',
  minWidth: 210,
  accessor: (charger) => {
    const status = get(charger, statusPath, '');
    const isOnline = get(charger, onlinePath);
    const value = status && status.toLowerCase();

    return isOnline ? value : CHARGER_STATUS_UNKNOWN;
  },
  Cell: ({ value }) => <StatusLabel status={value} />,
  Filter: ({ filter, onChange }) => <TableSelect options={chargerStatusOptions} onChange={onChange} value={filter ? filter.value : ''} />,
});

export const getProductTypesColumn = (productTypes, advancedSearchEnabled = false, uiLabels = {}) => ({
  Header: get(uiLabels, uiLabelsPaths.p_type, 'Product Type'),
  id: uiLabelsPaths.p_type,
  accessor: uiLabelsPaths.p_type,
  minWidth: 150,
  Filter: ({ filter, onChange }) => (
    <TableSelect options={getProductTypeOptions(productTypes)} onChange={onChange} value={filter ? filter.value : ''} />
  ),
  filterable: !advancedSearchEnabled,
});

export const getConditionColumn = (uiLabels, accessor, id = 'condition') => ({
  Header: get(uiLabels, uiLabelsPaths.c_condition, 'Charger Condition'),
  id,
  accessor,
  minWidth: 140,
  Filter: ({ filter, onChange }) => <TableSelect options={conditionOptions} onChange={onChange} value={filter ? filter.value : ''} />,
  Cell: ({ value }) => <StatusLabel status={value} />,
});

const getConnectorsOptions = (config) => {
  const options = [allMenuItem];

  const connectorsNumber = getConnectorsNumberToShow(config);

  for (let i = 1; i < connectorsNumber; i++) {
    options.push(
      <MenuItem key={i} value={i} title={`Connector ${i}`}>
        {`Connector ${i}`}
      </MenuItem>,
    );
  }

  options.push(
    <MenuItem key={0} value={0} title="Station">
      Station
    </MenuItem>,
  );

  return options;
};

export const getConnectorNameColumn = (config) => ({
  Header: 'Connector Name',
  id: 'connector_id',
  accessor: 'connector_name',
  minWidth: 150,
  sortable: false,
  Filter: ({ filter, onChange }) => (
    <TableSelect options={getConnectorsOptions(config)} onChange={onChange} value={filter ? filter.value : ''} />
  ),
});

export const sessionStatusesOpts = sortBy(
  [
    allMenuItem,
    Object.keys(sessionStatuses).map((key) => (
      <MenuItem key={key} value={[key]} title={sessionStatuses[key]}>
        {sessionStatuses[key]}
      </MenuItem>
    )),
  ],
  'props.primaryText',
);

const evPriorities = [
  {
    key: 'Bronze',
    label: '1 - Bronze',
  },
  {
    key: 'Silver',
    label: '2 - Silver',
  },
  {
    key: 'Gold',
    label: '3 - Gold',
  },
  {
    key: 'Platinum',
    label: '4 - Platinum',
  },
];

export const evPriorityOpts = [
  allMenuItem,
  ...evPriorities.map(({ key, label }) => (
    <MenuItem key={uuidv4()} value={key} title={label}>
      {label}
    </MenuItem>
  )),
];

const renderTableHeaderWithTooltip = (title, infoOption) => (
  <div className="tableHeaderWithTooltip ellipsis">
    <span className="tableHeaderWithTooltip__title">{title}</span>
    <InfoButtonComponent infoOption={infoOption} isAbsolute={false} />
  </div>
);

export const sessionStatusColumn = {
  Header: () => renderTableHeaderWithTooltip('Session Status', 'sessionStatusesState'),
  id: 'cs_status',
  minWidth: 200,
  className: 'nested-table',
  accessor: buildSessionStatus,
  Cell: ({ value }) => <div style={{ padding: '5px' }}>{value}</div>,
  Filter: ({ filter, onChange }) => <TableSelect options={sessionStatusesOpts} onChange={onChange} value={filter ? filter.value : ''} />,
};

export const getOptionsByCode = (options, code) => {
  const option = options.find((item) => item?.value === code);

  return option ? option.label : code;
};

export const OptionsFilter =
  (options) =>
  ({ filter, onChange }) => (
    <TableSelect options={makeSelectInputOptions(sortBy(options, 'label'))} onChange={onChange} value={filter ? filter.value : ''} />
  );

export const formatOptions = (options) => [{ value: '', label: 'All' }, ...options.filter((item) => item.value !== '')];

export const formatOptionsForErrorType = (options) => [
  { value: '', label: 'All' },
  { value: 'Empty', label: '' },
  ...options.filter((item) => item.value !== ''),
];

export const errorTypeColumn = (options, noFilter = false) => ({
  Header: () => renderTableHeaderWithTooltip('Error Type', 'errorType'),
  id: 'error_type',
  accessor: 'error_type',
  Cell: ({ value }) => <span>{getOptionsByCode(options, value)}</span>,
  Filter: noFilter ? undefined : OptionsFilter(formatOptionsForErrorType(options)),
});

export const errorSeverityColumn = (options, filterable, noFilter = false) => ({
  Header: () => 'Error Severity',
  id: 'severity',
  accessor: 'severity',
  Cell: ({ value }) => <span>{getOptionsByCode(options, value)}</span>,
  Filter: noFilter ? undefined : OptionsFilter(formatOptions(options)),
  filterable,
});

export const moduleNameColumn = (options, noFilter = false) => ({
  Header: () => 'Module Name',
  id: 'error_module',
  accessor: 'error_module',
  Cell: ({ value }) => <span>{getOptionsByCode(options, value)}</span>,
  Filter: noFilter ? undefined : OptionsFilter(formatOptions(options)),
});

export const evPriorityColumn = {
  Header: 'Priority',
  id: 'priority',
  minWidth: 200,
  className: 'nested-table',
  accessor: 'priority',
  Cell: ({ value }) => <div style={{ padding: '5px' }}>{statusLabels[value] || value}</div>,
  Filter: ({ filter, onChange }) => <TableSelect options={evPriorityOpts} onChange={onChange} value={filter ? filter.value : ''} />,
};

export const getProductSerialNumberColumn = (uiLabels) => ({
  Header: () => get(uiLabels, uiLabelsPaths.product_serial, 'Product Serial Number'),
  id: 'product_serial_number',
  className: 'nested-table',
  accessor: 'c_identity.product_serial_number',
  minWidth: 180,
});

export const sessionTypeColumn = {
  Header: 'Session Type',
  id: 'session_type',
  minWidth: 150,
  accessor: 'session_type',
  Filter: onTextFilter,
};

export const buildDynamicColumn = (field, uiLabels) => ({
  Header: get(uiLabels, field.replace('.', '/'), field),
  id: field,
  accessor: field,
  minWidth: 100,
  isDynamicField: true,
  Filter: onTextFilter,
});

export const getSessionsColumnsFromSettings = (sessionsColumns, config, excludedReportFields = []) => {
  const fields = get(config, 'chargerDetails.sessions.fields', []);

  if (fields.length > 0) {
    return fields
      .filter((field) => !excludedReportFields.includes(field))
      .map((field) => {
        const column = sessionsColumns.find((column) => field === column.id);

        if (!column) {
          return buildDynamicColumn(field, config.ui_labels);
        }

        return column;
      });
  }

  return sessionsColumns;
};

export const getChargerSessionColumns = (that, config, hideIdAndName) => {
  const filter = get(config, 'chargerDetails.sessions.fields', []);
  let sessionColumns = [
    {
      Header: get(config.ui_labels, uiLabelsPaths.c_uuid, 'Charger ID'),
      accessor: 'c_uuid',
      id: 'c_uuid',
      ...getColumnClasses({ isLink: true, isSticky: !isOnlyMobile }),
      Filter: onTextFilter,
      Cell: ({ value }) => (
        <span
          className={isDemo() ? 'centered' : 'link centered'}
          onClick={() => {
            !isDemo() && that.setState({ modalOpen: true, chargerId: value });
          }}
        >
          {value}
        </span>
      ),
      minWidth: 200,
    },
    {
      Header: get(config.ui_labels, uiLabelsPaths.c_name, 'Charger Name'),
      id: 'charger_name',
      accessor: 'charger_name',
      minWidth: 200,
      Filter: onTextFilter,
    },
    getConnectorNameColumn(config),
    {
      Header: 'User IdTag',
      id: 'id_tag',
      accessor: 'id_tag',
      minWidth: 100,
      Filter: onTextFilter,
    },
    {
      Header: 'Session ID',
      accessor: 'session_id',
      id: 'id',
      Filter: onTextFilter,
      minWidth: 200,
    },
    sessionTypeColumn,
    {
      Header: 'EV ID',
      id: 'ev_id',
      minWidth: 150,
      accessor: 'ev_id',
      Filter: onTextFilter,
    },
    {
      Header: 'Charging Mode',
      id: 'charge_mode',
      minWidth: 200,
      accessor: (d) => {
        const mode = get(d, 'charge_mode');

        return mode ? get(chargingModes, upperCase(mode)) : '';
      },
      Filter: ({ filter, onChange }) => (
        <TableSelect options={buildChargingModes()} onChange={onChange} value={filter ? filter.value : ''} />
      ),
    },
    {
      Header: 'Start Time (UTC)',
      id: 'start_time',
      minWidth: 200,
      accessor: 'start_time',
      Cell: ({ value }) => (isDemo() ? moment.utc(value).format(iotOnDateFormat) : moment.unix(value).utc().format(iotOnDateFormat)),
      Filter: onDatePickerFilter,
    },
    {
      Header: 'End Time (UTC)',
      id: 'end_time',
      minWidth: 200,
      accessor: 'end_time',
      Cell: ({ value }) => {
        if (value) {
          return isDemo() ? moment.utc(value).format(iotOnDateFormat) : moment.unix(value).utc().format(iotOnDateFormat);
        }

        return '';
      },
      Filter: onDatePickerFilter,
    },
    {
      Header: 'Duration (hh:mm:ss)',
      accessor: 'duration',
      id: 'duration',
      filterable: false,
      minWidth: 200,
      Cell: ({ value }) => (value ? getDurationWithMainHours(value, true) : ''),
    },
    {
      Header: 'Energy Total (kWh)',
      accessor: 'energy',
      id: 'energy',
      Filter: onTextFilter,
      minWidth: 200,
      Cell: ({ value }) => setEnergyOutput(value),
    },
    sessionStatusColumn,
  ];
  const nameAndIdFilter = ['c_uuid'];

  if (!isEmpty(filter)) {
    sessionColumns.push(
      {
        Header: 'Energy Positive (kWh)',
        accessor: 'positive_energy',
        id: 'positive_energy',
        Filter: onTextFilter,
        Cell: ({ value }) => setEnergyOutput(value),
        minWidth: 200,
      },
      {
        Header: 'Energy Negative (kWh)',
        accessor: 'negative_energy',
        id: 'negative_energy',
        Cell: ({ value }) => setEnergyOutput(value),
        Filter: onTextFilter,
        minWidth: 200,
      },
    );
  }

  if (hideIdAndName) {
    sessionColumns = sessionColumns.filter((item) => !nameAndIdFilter.includes(item));
  }

  if (size(filter)) {
    sessionColumns = sessionColumns.filter((column) => filter.includes(column.id));
  }

  return sessionColumns;
};
